import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { Button, Grid, useMediaQuery } from "@mui/material";
import * as Yup from "yup";
import moment from "moment";
import { useFormik } from "formik";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import { billingSubscriptionTypes } from "common/enums";
import BaseTable from "Components/Common/BaseTable";
import DatePicker from "Components/Common/DatePicker";
import SelectField from "Components/Common/SelectField";

import { isAuthorizedUser } from "utils";

import { useLazyGetMissingReadingsQuery } from "apis/Billing";
import { useLazyDownloadExcelQuery } from "slices/api";

const columns = [
  { title: "Consumer Number", field: "consumerNumber" },
  { title: "Consumer Name", field: "consumerName" },
  { title: "Meter Number", field: "meterNumber" },
  { title: "Consumer Address", field: "consumerAddress" },
];

const startDateOfCurrentMonth = moment().startOf("month");

const MissingReadings = ({ t }) => {
  const { roles } = useSelector((state) => state.Login);

  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const [subscriptionType, setSubscriptionType] = useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();

  const [
    getMissingReadingsData,
    { data: { data: missingReadingsData } = {}, isLoading, isCaching },
  ] = useLazyGetMissingReadingsQuery();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const formik = useFormik({
    initialValues: {
      fromDate: startDateOfCurrentMonth,
      toDate: "",
    },
    validationSchema: Yup.object({
      fromDate: Yup.date().required("Start Date is Required"),
    }),
    onSubmit: async ({ fromDate, toDate }) => {
      try {
        await getMissingReadingsData({
          subscriptionType,
          fromDate: fromDate.format("YYYY-MM-DD"),
          toDate: toDate ? toDate.format("YYYY-MM-DD") : "",
        });
        handleResetPage();
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    values: { fromDate, toDate },
    setFieldValue,
  } = formik;

  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      billingSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [billingSubscriptionTypes]
  );

  const downloadMissingReadingsReport = async () => {
    if (fromDate) {
      await downloadExcel({
        endpoint: "/report/missing-meter-reading-excel",
        query: {
          subscriptionType,
          fromDate: fromDate.format("YYYY-MM-DD"),
          toDate: toDate ? toDate.format("YYYY-MM-DD") : "",
        },
        sheetName: "Missing Readings",
      });
    }
  };

  const clearBtnHandler = async () => {
    try {
      setFieldValue("fromDate", startDateOfCurrentMonth);
      setFieldValue("toDate", "");
      await getMissingReadingsData({
        subscriptionType,
        fromDate: startDateOfCurrentMonth.format("YYYY-MM-DD"),
      });
      handleResetPage();
    } catch (error) {}
  };

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length) {
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions]);

  useEffect(() => {
    (async () => {
      if (subscriptionType) {
        await getMissingReadingsData({
          subscriptionType,
          fromDate: startDateOfCurrentMonth.format("YYYY-MM-DD"),
        });
      }
    })();
  }, [subscriptionType]);

  return (
    <>
      <BreadCrumb title={t("Missing Readings")} pageTitle={t("Billing")} />
      <Backdrop open={isLoading || isCaching || isExcelFileDownloading} />
      <form onSubmit={handleSubmit}>
        <Grid container columnSpacing={2} className="mt-2">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => setSubscriptionType(value)}
              value={
                updatedBillingSubscriptionOptions.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
              options={updatedBillingSubscriptionOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DatePicker
              label={t("Start Date")}
              format="DD-MM-YYYY"
              name="fromDate"
              maxDate={toDate}
              formik={formik}
              disableFuture
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DatePicker
              name="toDate"
              label={t("End Date")}
              formik={formik}
              format="DD-MM-YYYY"
              minDate={fromDate}
              disableFuture
            />
          </Grid>
        </Grid>

        <Grid item lg={5}>
          <Button variant="outlined" className="mb-2 me-2" type="submit">
            {t("Search")}
          </Button>
          <Button
            variant="outlined"
            onClick={clearBtnHandler}
            className="mb-2 me-2"
          >
            {t("Clear")}
          </Button>
          <Button
            variant="contained"
            className="mb-2 me-2"
            onClick={downloadMissingReadingsReport}
          >
            {t("Download")}
          </Button>
        </Grid>
      </form>
      <BaseTable
        data={missingReadingsData}
        columns={columns}
        pageSize={20}
        displaySrNo
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={
          sm
            ? "calc(100vh - 350px)"
            : md
            ? "calc(100vh - 300px)"
            : "calc(100vh - 250px)"
        }
      />
    </>
  );
};

MissingReadings.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(MissingReadings);
